import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bar, Line, Pie } from 'react-chartjs-2';
import API_URL from '../../config/api';
import { useAuth } from '../../context/AuthContext';
import { apiFetch } from '../../utils/apiFetch';
import { showToast } from '../../utils/toast';
import { ListPagination, paginateList } from '../../components/ListPagination';
import '../../styles/listPage.css';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  ArcElement,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';

ChartJS.register(
  CategoryScale, LinearScale, BarElement,
  PointElement, LineElement, ArcElement,
  Tooltip, Legend, Filler
);

const GREEN = '#10b981';
const GREEN_DARK = '#059669';
const GREEN_LIGHT = 'rgba(16,185,129,0.12)';
const BLUE = '#2563eb';
const RED = '#ef4444';
const AMBER = '#f59e0b';

const CHART_COLORS = [GREEN,'#2563eb','#f59e0b','#ef4444','#8b5cf6','#06b6d4','#ec4899','#84cc16'];

const fmt = (n: number) => `£${Number(n).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const Analytics: React.FC = () => {
  const navigate = useNavigate();
  const { token } = useAuth();

  const [data, setData] = useState<any>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('6m');
  const [stockVal, setStockVal] = useState<any>(null);
  const [customerPage, setCustomerPage] = useState(1);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        if (!token) { setError('Not authenticated'); setLoading(false); return; }
        const [res, svRes] = await Promise.all([
          apiFetch(`${API_URL}/analytics/summary?period=${period}`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          apiFetch(`${API_URL}/analytics/stock-valuation`, {
            headers: { Authorization: `Bearer ${token}` }
          }).catch(() => null),
        ]);
        if (!res.ok) throw new Error('Failed');
        setData(await res.json());
        if (svRes?.ok) setStockVal(await svRes.json());
      } catch {
        setError('Failed to load analytics data. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token, period]);

  useEffect(() => {
    setCustomerPage(1);
  }, [period]);

  const handleDownloadCSV = async () => {
    try {
      const res = await apiFetch(`${API_URL}/analytics/dashboard/export/csv`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) { showToast('Failed to download CSV'); return; }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url;
      a.download = 'analytics.csv'; document.body.appendChild(a);
      a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch { showToast('Failed to download CSV'); }
  };

  const monthlyLabels = data?.monthlyLabels?.length
    ? data.monthlyLabels
    : Array.from({ length: data?.monthlyRevenue?.length || 6 }, (_, i) => {
        const len = data?.monthlyRevenue?.length || 6;
        return i === len - 1 ? 'This Month' : `M-${len - 1 - i}`;
      });

  const monthlyData = data?.monthlyRevenue || Array(monthlyLabels.length).fill(0);

  const revenueChart = {
    labels: monthlyLabels,
    datasets: [{
      label: 'Revenue (£)',
      data: monthlyData,
      backgroundColor: CHART_COLORS.map(c => c + '33'),
      borderColor: CHART_COLORS,
      borderWidth: 2,
      borderRadius: 6,
    }]
  };

  const ordersChart = {
    labels: ['4d ago','3d ago','2d ago','Yesterday','Today'],
    datasets: [{
      label: 'Orders',
      data: data?.dailyOrders || Array(5).fill(0),
      borderColor: GREEN,
      backgroundColor: GREEN_LIGHT,
      fill: true,
      tension: 0.4,
      pointBackgroundColor: GREEN,
      pointRadius: 5,
    }]
  };

  const productChart = data?.topProducts?.length ? {
    labels: data.topProducts.map((p: any) => p.name),
    datasets: [{
      data: data.topProducts.map((p: any) => p.sales),
      backgroundColor: CHART_COLORS,
      borderWidth: 0,
    }]
  } : null;

  const revCatChart = data?.revenueByCategory?.length ? {
    labels: data.revenueByCategory.map((r: any) => r.category),
    datasets: [{
      label: 'Income (£)',
      data: data.revenueByCategory.map((r: any) => r.total),
      backgroundColor: GREEN,
      borderRadius: 4,
    }]
  } : null;

  const expCatChart = data?.expenseByCategory?.length ? {
    labels: data.expenseByCategory.map((r: any) => r.category),
    datasets: [{
      label: 'Expense (£)',
      data: data.expenseByCategory.map((r: any) => r.total),
      backgroundColor: RED,
      borderRadius: 4,
    }]
  } : null;

  const totalRevenue = data?.monthlyRevenue?.reduce((s: number, n: number) => s + n, 0) || 0;
  const totalOrders = data?.dailyOrders?.reduce((s: number, n: number) => s + n, 0) || 0;
  const topCustomer = data?.topCustomers?.[0];
  const customerKpi = data?.customerCount ?? data?.topCustomers?.length ?? 0;
  const topCustomers = data?.topCustomers || [];
  const { pages: customerPages, safePage: safeCustomerPage, slice: paginatedCustomers } =
    paginateList(topCustomers, customerPage);

  const chartOpts: any = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false } },
    scales: {
      x: { grid: { color: 'rgba(0,0,0,0.04)' }, ticks: { color: '#6b7280', font: { size: 11 } } },
      y: { grid: { color: 'rgba(0,0,0,0.04)' }, ticks: { color: '#6b7280', font: { size: 11 } } }
    }
  };

  const pieOpts: any = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { position: 'bottom', labels: { color: '#374151', font: { size: 11 }, padding: 12 } } }
  };

  return (
    <div style={s.page}>
      {/* Top bar */}
      <div style={s.topbar}>
        <div style={s.topbarLeft}>
          <button style={s.backBtn} onClick={() => navigate(-1)}>← Back</button>
          <div>
            <h1 style={s.pageTitle}>Analytics</h1>
            <p style={s.pageSub}>Real-time business performance overview</p>
          </div>
        </div>
        <div style={s.topbarRight}>
          <select style={s.select} value={period} onChange={e => setPeriod(e.target.value)}>
            <option value="3m">Last 3 Months</option>
            <option value="6m">Last 6 Months</option>
            <option value="1y">Last Year</option>
            <option value="all">All Time</option>
          </select>
          <button style={s.csvBtn} onClick={handleDownloadCSV}>⬇ Export CSV</button>
          <button style={s.refreshBtn} onClick={() => window.location.reload()}>↺ Refresh</button>
        </div>
      </div>

      {error && <div style={s.errorBanner}>⚠️ {error}</div>}
      {data?.degraded?.length > 0 && (
        <div style={s.warnBanner}>⚠️ Partial data available — {data.degraded.join(', ')} could not load.</div>
      )}

      {loading ? (
        <div style={s.loadingWrap}>
          <div style={s.spinner} />
          <p style={{ color: '#6b7280', marginTop: 16 }}>Loading analytics...</p>
        </div>
      ) : (
        <>
          {/* KPI Cards */}
          <div style={s.kpiGrid}>
            <div style={{ ...s.kpiCard, borderTopColor: GREEN }}>
              <div style={s.kpiIcon}>💰</div>
              <div style={s.kpiValue}>{fmt(totalRevenue)}</div>
              <div style={s.kpiLabel}>Total Revenue</div>
            </div>
            <div style={{ ...s.kpiCard, borderTopColor: BLUE }}>
              <div style={s.kpiIcon}>📦</div>
              <div style={s.kpiValue}>{totalOrders}</div>
              <div style={s.kpiLabel}>Orders (5 days)</div>
            </div>
            <div style={{ ...s.kpiCard, borderTopColor: AMBER }}>
              <div style={s.kpiIcon}>👑</div>
              <div style={s.kpiValue}>{topCustomer?.name || '—'}</div>
              <div style={s.kpiLabel}>Top Customer</div>
            </div>
            <div style={{ ...s.kpiCard, borderTopColor: '#8b5cf6' }}>
              <div style={s.kpiIcon}>🧾</div>
              <div style={s.kpiValue}>{customerKpi}</div>
              <div style={s.kpiLabel}>{data?.customerCount != null ? 'Total Customers' : 'Top Customers'}</div>
            </div>
          </div>

          {/* Stock Valuation Section */}
          {stockVal && (
            <div style={{ marginBottom: 28 }}>
              <h2 style={{ fontSize: 16, fontWeight: 700, color: '#111827', margin: '0 0 12px' }}>📦 Stock Valuation</h2>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12 }}>
                {[
                  { icon: '💷', label: 'Cost Value', value: fmt(stockVal.cost_value), color: '#1e3a8a', bg: '#eff6ff', note: 'At purchase cost' },
                  { icon: '🏷️', label: 'Retail Value', value: fmt(stockVal.retail_value), color: '#059669', bg: '#f0fdf4', note: 'At retail price' },
                  { icon: '🏭', label: 'Wholesale Value', value: fmt(stockVal.wholesale_value), color: '#7c3aed', bg: '#f5f3ff', note: 'At wholesale price' },
                  { icon: '📈', label: 'Potential GP', value: stockVal.potential_gp_pct != null ? `${stockVal.potential_gp_pct}%` : '—', color: '#d97706', bg: '#fffbeb', note: 'Retail margin on cost' },
                  { icon: '✅', label: 'In Stock', value: String(stockVal.in_stock_count), color: '#16a34a', bg: '#f0fdf4', note: `of ${stockVal.total_products} SKUs` },
                  { icon: '⚠️', label: 'Low / Out', value: `${stockVal.low_stock_count} / ${stockVal.out_of_stock_count}`, color: '#dc2626', bg: '#fef2f2', note: 'Below min / zero stock' },
                ].map(({ icon, label, value, color, bg, note }) => (
                  <div key={label} style={{ background: bg, border: `1.5px solid ${color}22`, borderRadius: 10, padding: '14px 16px' }}>
                    <div style={{ fontSize: 20, marginBottom: 6 }}>{icon}</div>
                    <div style={{ fontSize: 18, fontWeight: 800, color }}>{value}</div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginTop: 2 }}>{label}</div>
                    <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>{note}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Charts Row 1 */}
          <div style={s.chartsRow}>
            <div style={s.chartCard}>
              <div style={s.chartHeader}>
                <span style={s.chartTitle}>📈 Monthly Revenue</span>
              </div>
              <div style={s.chartBody}>
                <Bar data={revenueChart} options={chartOpts} />
              </div>
            </div>
            <div style={s.chartCard}>
              <div style={s.chartHeader}>
                <span style={s.chartTitle}>📊 Daily Orders</span>
              </div>
              <div style={s.chartBody}>
                <Line data={ordersChart} options={chartOpts} />
              </div>
            </div>
          </div>

          {/* Charts Row 2 */}
          <div style={s.chartsRow}>
            <div style={s.chartCard}>
              <div style={s.chartHeader}>
                <span style={s.chartTitle}>🟢 Income by Category</span>
              </div>
              <div style={s.chartBody}>
                {revCatChart
                  ? <Bar data={revCatChart} options={{ ...chartOpts, plugins: { legend: { display: false } } }} />
                  : <Empty text="No income transactions yet" />}
              </div>
            </div>
            <div style={s.chartCard}>
              <div style={s.chartHeader}>
                <span style={s.chartTitle}>🔴 Expenses by Category</span>
              </div>
              <div style={s.chartBody}>
                {expCatChart
                  ? <Bar data={expCatChart} options={{ ...chartOpts, plugins: { legend: { display: false } } }} />
                  : <Empty text="No expense transactions yet" />}
              </div>
            </div>
          </div>

          {/* Charts Row 3 */}
          <div style={s.chartsRow}>
            <div style={{ ...s.chartCard, flex: '0 0 360px' }}>
              <div style={s.chartHeader}>
                <span style={s.chartTitle}>🏆 Top Products</span>
              </div>
              <div style={{ ...s.chartBody, height: 300 }}>
                {productChart
                  ? <Pie data={productChart} options={pieOpts} />
                  : <Empty text="No product sales data yet" />}
              </div>
            </div>

            {/* Top Customers */}
            <div style={{ ...s.chartCard, flex: 1 }}>
              <div style={s.chartHeader}>
                <span style={s.chartTitle}>👑 Top Customers by Revenue</span>
              </div>
              {!data?.topCustomers?.length ? (
                <Empty text="No customer data yet" />
              ) : (
                <>
                <ListPagination
                  total={topCustomers.length}
                  filtered={topCustomers.length}
                  page={safeCustomerPage}
                  pages={customerPages}
                  onPageChange={setCustomerPage}
                  noun="customers"
                />
                <div className="list-table-panel">
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #f3f4f6' }}>
                      {['#','Customer','Revenue','Invoices','Avg'].map(h => (
                        <th key={h} style={s.th}>{h}</th>
                      ))}
                      <th style={s.th}>Share</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedCustomers.map((c: any, i: number) => {
                      const globalIndex = (safeCustomerPage - 1) * 10 + i;
                      const maxR = data.topCustomers[0]?.totalRevenue || 1;
                      const pct = Math.round((c.totalRevenue / maxR) * 100);
                      const medal = globalIndex === 0 ? '🥇' : globalIndex === 1 ? '🥈' : globalIndex === 2 ? '🥉' : `#${globalIndex+1}`;
                      return (
                        <tr key={globalIndex} style={{ borderBottom: '1px solid #f9fafb' }}>
                          <td style={s.td}><span style={{ fontWeight: 700 }}>{medal}</span></td>
                          <td style={{ ...s.td, fontWeight: 600 }}>{c.name || 'Unknown'}</td>
                          <td style={{ ...s.td, color: GREEN, fontWeight: 700 }}>{fmt(c.totalRevenue)}</td>
                          <td style={s.td}>{c.invoiceCount}</td>
                          <td style={s.td}>{fmt(c.avgInvoice)}</td>
                          <td style={{ ...s.td, width: 120 }}>
                            <div style={{ background: '#f3f4f6', borderRadius: 4, height: 8 }}>
                              <div style={{ background: GREEN, width: `${pct}%`, height: '100%', borderRadius: 4, transition: 'width 0.6s ease' }} />
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                </div>

                <div className="list-mobile-cards">
                  {paginatedCustomers.map((c: any, i: number) => {
                    const globalIndex = (safeCustomerPage - 1) * 10 + i;
                    const medal = globalIndex === 0 ? '🥇' : globalIndex === 1 ? '🥈' : globalIndex === 2 ? '🥉' : `#${globalIndex+1}`;
                    return (
                      <div key={globalIndex} className="list-mobile-card">
                        <div className="list-mobile-card-header">
                          <div style={{ minWidth: 0, flex: 1 }}>
                            <div className="list-mobile-card-title">{medal} {c.name || 'Unknown'}</div>
                            <div className="list-mobile-card-meta">{c.invoiceCount} invoices · avg {fmt(c.avgInvoice)}</div>
                          </div>
                          <span style={{ color: GREEN, fontWeight: 700 }}>{fmt(c.totalRevenue)}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
                </>
              )}
            </div>
          </div>
        </>
      )}

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
};

const Empty = ({ text }: { text: string }) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#9ca3af', fontSize: 14, flexDirection: 'column', gap: 8 }}>
    <span style={{ fontSize: 32 }}>📭</span>
    <span>{text}</span>
  </div>
);

const s: any = {
  page: { maxWidth: 1400, margin: '0 auto', padding: '32px 24px 60px', background: '#f8fafc', minHeight: '100vh', fontFamily: "'DM Sans', system-ui, sans-serif" },
  topbar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 28, flexWrap: 'wrap', gap: 16 },
  topbarLeft: { display: 'flex', alignItems: 'center', gap: 16 },
  topbarRight: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' },
  backBtn: { background: 'white', border: '1px solid #e5e7eb', borderRadius: 8, padding: '8px 14px', cursor: 'pointer', fontSize: 13, color: '#374151', fontWeight: 500 },
  pageTitle: { margin: 0, fontSize: 24, fontWeight: 700, color: '#111827', letterSpacing: '-0.5px' },
  pageSub: { margin: '2px 0 0', fontSize: 13, color: '#6b7280' },
  select: { border: '1px solid #e5e7eb', borderRadius: 8, padding: '8px 12px', fontSize: 13, color: '#374151', background: 'white', cursor: 'pointer' },
  csvBtn: { background: GREEN, color: 'white', border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  refreshBtn: { background: 'white', border: '1px solid #e5e7eb', borderRadius: 8, padding: '8px 14px', fontSize: 13, color: '#374151', cursor: 'pointer' },
  errorBanner: { background: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca', borderRadius: 10, padding: '12px 16px', marginBottom: 20, fontSize: 14 },
  warnBanner: { background: '#fffbeb', color: '#92400e', border: '1px solid #fde68a', borderRadius: 10, padding: '12px 16px', marginBottom: 20, fontSize: 14 },
  loadingWrap: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 400 },
  spinner: { width: 40, height: 40, border: '4px solid #e5e7eb', borderTopColor: GREEN, borderRadius: '50%', animation: 'spin 0.8s linear infinite' },
  kpiGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16, marginBottom: 24 },
  kpiCard: { background: 'white', borderRadius: 12, padding: '20px 24px', borderTop: '3px solid transparent', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', transition: 'transform 0.2s', cursor: 'default' },
  kpiIcon: { fontSize: 24, marginBottom: 8 },
  kpiValue: { fontSize: 22, fontWeight: 700, color: '#111827', letterSpacing: '-0.5px', marginBottom: 4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  kpiLabel: { fontSize: 12, color: '#6b7280', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px' },
  chartsRow: { display: 'flex', gap: 16, marginBottom: 20, flexWrap: 'wrap' },
  chartCard: { background: 'white', borderRadius: 12, flex: 1, minWidth: 300, boxShadow: '0 1px 3px rgba(0,0,0,0.06)', overflow: 'hidden' },
  chartHeader: { padding: '16px 20px 0', borderBottom: '1px solid #f3f4f6' },
  chartTitle: { fontSize: 14, fontWeight: 600, color: '#111827', display: 'block', paddingBottom: 12 },
  chartBody: { padding: '20px', height: 260, position: 'relative' },
  th: { padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap' },
  td: { padding: '11px 14px', fontSize: 13, color: '#374151' },
};

export default Analytics;
